import React, { useState, useEffect } from 'react';
import { ChatSidebar, ChatDisplay, MessageInput } from '@components/GenerateContracts';

import {
  OuterContainer,
  LayoutRow,
  SidebarColumn,
  MainContentColumn,
  Box,
} from './GenerateContractsContainer.styles';
import { workflowApi } from '@api/api';

const GenerateContractsContainer = () => {
  const [messages, setMessages] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWorkflows = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await workflowApi.getWorkflows();
        setWorkflows(data || []);
      } catch (err) {
        console.error('Error fetching workflows:', err);
        setError('Failed to load contract sessions.');
        setWorkflows([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkflows();
  }, []);

  useEffect(() => {
    if (!selectedWorkflowId) {
      setMessages([]);
      return;
    }

    const fetchMessages = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await workflowApi.getWorkflowById(selectedWorkflowId);
        const processedMessages = (data || []).map(message => {
          if (message.type === 'doc') {
            let parsedContent = JSON.parse(message.content);
            return {
              document: parsedContent.document,
              variables: parsedContent.variables,
              ...message
            };
          }

          return message;
        });
        setMessages(processedMessages);
      } catch (err) {
        console.error(`Error fetching messages for workflow ${selectedWorkflowId}:`, err);
        setError('Failed to load messages for this session.');
        setMessages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [selectedWorkflowId]);

  const handleNewWorkflow = () => {
    setSelectedWorkflowId(null);
    setMessages([]);
    setError(null);
  };

  const handleSelectWorkflow = (workflowId) => {
    if (workflowId === selectedWorkflowId) return;
    setSelectedWorkflowId(workflowId);
    setError(null);
  };

  const handleSendMessage = async (inputText) => {
    if (!inputText.trim()) return;

    setError(null);
    setIsLoading(true);

    const userMessage = { role: 'user', content: inputText, timestamp: new Date().toISOString() };
    setMessages(prevMessages => [...prevMessages, userMessage]);

    try {
      let response;
      if (!selectedWorkflowId) {
        const loadingMessage = { type: 'loading', id: `loading-${Date.now()}` };
        setMessages(prevMessages => [...prevMessages, loadingMessage]);

        response = await workflowApi.newWorkflow({ text: inputText });

        setSelectedWorkflowId(response.workflow.id);
        setWorkflows(prev => [response.workflow, ...prev]);

        setMessages(prevMessages => {
          const newMessages = prevMessages.filter(msg => msg.type !== 'loading');

          if (response.response.type === 'doc') {
            const oldMessage = {
              role: 'assistant',
              type: response.response.type,
              document: response.response.document,
              variables: response.response.variables
            };

            return [...newMessages, oldMessage]; 
          }
        });
      } else {
        const loadingMessage = { type: 'loading', id: `loading-${Date.now()}` };
        setMessages(prevMessages => [...prevMessages, loadingMessage]);

        response = await workflowApi.newInteraction(selectedWorkflowId, inputText);

        setMessages(prevMessages => {
          const newMessages = prevMessages.filter(msg => msg.type !== 'loading');

          if (response.response.type === 'doc') {
            const oldMessage = {
              role: 'assistant',
              type: response.response.type,
              document: response.response.document,
              variables: response.response.variables
            };

            return [...newMessages, oldMessage];
          }
        });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message or get response.');
      const errorMessage = { type: 'error', content: 'Failed to get response from the server.', timestamp: new Date().toISOString() };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
  <Box >
    <OuterContainer>
      <LayoutRow>
        <SidebarColumn>
          <ChatSidebar
            workflows={workflows}
            selectedWorkflowId={selectedWorkflowId}
            onSelectWorkflow={handleSelectWorkflow}
            onNewWorkflow={handleNewWorkflow}
            isLoading={isLoading && workflows.length === 0}
          />
        </SidebarColumn>
        <MainContentColumn>
          <ChatDisplay
            messages={messages}
          />
        </MainContentColumn>
      </LayoutRow>
      <MessageInput
        onSendMessage={handleSendMessage}
        isLoading={isLoading && messages.some(m => m.type === 'loading')}
      />
    </OuterContainer>
  </Box>
  );
};

export default GenerateContractsContainer;
